<template>
    <v-card rounded="lg" variant="outlined">
        <!-- Toolbar -->
        <div class="d-flex flex-wrap align-center ga-3 pa-4 pb-2">
            <div class="text-subtitle-1 font-weight-bold">{{ title }}</div>
            <v-chip-group :model-value="quickChip" class="ml-1" @update:model-value="onQuickChip">
                <v-chip value="all" size="small" variant="outlined" filter color="primary">All shifts</v-chip>
                <v-chip value="open" size="small" variant="outlined" filter color="success">On shift now</v-chip>
                <v-chip value="review" size="small" variant="outlined" filter color="error">
                    Needs review
                    <v-badge v-if="needsReviewCount" :content="needsReviewCount" color="error" inline/>
                </v-chip>
            </v-chip-group>
            <v-spacer/>
            <v-tooltip location="top" :text="exportBlocked" :disabled="!exportBlocked">
                <template #activator="{props: tip}">
                    <span v-bind="tip">
                        <v-btn size="small" variant="tonal" prepend-icon="mdi-download" :loading="exporting"
                               :disabled="!!exportBlocked" @click="exportCsv">
                            Export CSV
                        </v-btn>
                    </span>
                </template>
            </v-tooltip>
            <v-btn icon="mdi-refresh" size="small" variant="text" :loading="loading" @click="fetchRows"
                   aria-label="Refresh shifts"/>
        </div>

        <!-- Filters. Each wraps onto its own line on a phone. -->
        <v-row dense class="px-4 pb-1">
            <v-col cols="12" sm="6" :md="driverId ? 3 : 2">
                <!-- "On shift now" is not cut by date. -->
                <v-text-field v-if="isQueue" model-value="All dates" label="Dates" density="compact"
                              variant="outlined" hide-details disabled/>
                <v-select v-else v-model="preset" :items="RANGE_PRESETS" label="Dates" density="compact"
                          variant="outlined" hide-details/>
            </v-col>
            <template v-if="preset === 'custom' && !isQueue">
                <v-col cols="6" sm="3" md="2">
                    <v-text-field v-model="customFrom" type="date" label="From" density="compact" variant="outlined"
                                  hide-details :max="customTo || undefined"/>
                </v-col>
                <v-col cols="6" sm="3" md="2">
                    <v-text-field v-model="customTo" type="date" label="To" density="compact" variant="outlined"
                                  hide-details :min="customFrom || undefined" :error="customInvalid"/>
                </v-col>
            </template>
            <v-col cols="12" sm="6" :md="driverId ? 3 : 2">
                <v-select v-model="status" :items="STATUS_FILTERS" label="Status" density="compact"
                          variant="outlined" hide-details/>
            </v-col>
            <v-col cols="12" sm="6" :md="driverId ? 3 : 2">
                <v-autocomplete v-model="vehicle" :items="vehicles" item-title="label" item-value="id"
                                label="Vehicle" density="compact" variant="outlined" hide-details clearable
                                :loading="vehiclesLoading" no-data-text="No vehicles"/>
            </v-col>
            <v-col v-if="!driverId" cols="12" sm="6" md="2">
                <v-text-field v-model="search" label="Driver name" density="compact" variant="outlined"
                              hide-details clearable prepend-inner-icon="mdi-magnify" maxlength="100"/>
            </v-col>
            <v-col cols="12" sm="6" :md="driverId ? 3 : 2" class="d-flex align-center">
                <v-btn variant="text" size="small" :prepend-icon="sort === 'asc' ? 'mdi-sort-ascending' : 'mdi-sort-descending'"
                       @click="sort = sort === 'asc' ? 'desc' : 'asc'">
                    {{ sort === 'asc' ? 'Oldest first' : 'Newest first' }}
                </v-btn>
            </v-col>
        </v-row>

        <v-alert v-if="customInvalid" type="warning" variant="tonal" density="compact" class="mx-4 my-2">
            The end date is before the start date.
        </v-alert>

        <!-- Totals for everything the filters match, not just this page -->
        <div v-if="totals && !loadError" class="d-flex flex-wrap ga-2 px-4 py-2 totals-strip">
            <v-chip size="small" variant="tonal" prepend-icon="mdi-format-list-numbered">
                {{ totals.shifts }} {{ totals.shifts === 1 ? 'shift' : 'shifts' }}
            </v-chip>
            <v-chip size="small" variant="tonal" color="primary" prepend-icon="mdi-briefcase-clock-outline">
                {{ fmtDuration(totals.worked_seconds) }} worked
            </v-chip>
            <v-chip size="small" variant="tonal" prepend-icon="mdi-coffee-outline">
                {{ fmtDuration(totals.break_seconds) }} breaks
            </v-chip>
            <v-chip v-if="!driverId" size="small" variant="tonal" prepend-icon="mdi-account-multiple-outline">
                {{ totals.drivers }} {{ totals.drivers === 1 ? 'driver' : 'drivers' }}
            </v-chip>
            <div v-if="vehicle" class="text-caption text-medium-emphasis w-100">
                Totals include the full shift, even if the driver changed trucks.
            </div>
        </div>

        <v-progress-linear :active="loading && rows.length > 0" indeterminate color="primary" height="2"/>

        <!-- Shifts to review in this search while the list shows something else -->
        <v-alert v-if="status !== 'review' && status !== 'open' && reviewInSearch" type="warning" variant="tonal" density="compact"
                 class="mx-4 my-2" icon="mdi-alert-circle-outline">
            {{ reviewInSearch === 1 ? '1 shift needs' : `${reviewInSearch} shifts need` }} review in this search.
            <template #append>
                <v-btn size="small" variant="text" color="warning" @click="showReview">Show</v-btn>
            </template>
        </v-alert>

        <v-alert v-if="loadError" type="error" variant="tonal" density="compact" class="mx-4 my-2">
            {{ loadError }}
            <template #append>
                <v-btn size="small" variant="text" @click="fetchRows">Retry</v-btn>
            </template>
        </v-alert>

        <!-- Phone: one stacked row per shift, no sideways scroll -->
        <v-list v-if="rows.length && compact" class="py-0" lines="two">
            <template v-for="s in rows" :key="s.id">
                <v-list-item :active="expanded === s.id" color="primary" @click="toggle(s.id)"
                             :aria-expanded="expanded === s.id"
                             :aria-label="`Shift on ${f.fmtDay(s.started_at)}, ${shiftStatus(s).label}`">
                    <v-list-item-title class="d-flex align-center ga-2">
                        <span class="text-no-wrap">{{ f.fmtDay(s.started_at) }}</span>
                        <v-spacer/>
                        <span class="font-weight-medium text-no-wrap">{{ fmtDuration(s.worked_seconds) }}</span>
                    </v-list-item-title>
                    <v-list-item-subtitle class="mt-1">
                        <div v-if="showDriver" class="text-truncate">{{ s.driver_name || 'Driver' }}</div>
                        <div>
                            {{ f.fmtTime(s.started_at) }} to
                            {{ s.ended_at ? f.fmtTimeAfter(s.ended_at, s.started_at) : 'now' }}
                            <span v-if="s.asset_label"> &middot; {{ s.asset_label }}</span>
                        </div>
                    </v-list-item-subtitle>
                    <template #append>
                        <v-chip size="x-small" :color="shiftStatus(s).color" variant="tonal" class="ml-2">
                            {{ shiftStatus(s).label }}
                        </v-chip>
                    </template>
                </v-list-item>
                <div v-if="expanded === s.id" class="detail-holder-phone">
                    <div v-if="showDriver" class="px-4 pt-2">
                        <router-link class="text-primary text-decoration-none text-body-2"
                                     :to="{path: `/drivers/${s.driver_id}`, query: {tab: 'shifts'}}">
                            Open {{ s.driver_name || 'driver' }}
                        </router-link>
                    </div>
                    <ShiftDetail :shift-id="s.id" :company-id="companyId" @reviewed="onReviewed"/>
                </div>
                <v-divider/>
            </template>
        </v-list>

        <v-table v-else-if="rows.length" hover density="comfortable" class="shift-table">
            <thead>
            <tr>
                <th style="width: 40px"><span class="d-sr-only">Open</span></th>
                <th class="text-left">Date</th>
                <th v-if="showDriver" class="text-left">Driver</th>
                <th class="text-left">Time</th>
                <th class="text-left">Vehicle</th>
                <th class="text-right">Worked</th>
                <th class="text-right">Breaks</th>
                <th class="text-left">Status</th>
            </tr>
            </thead>
            <tbody>
            <template v-for="s in rows" :key="s.id">
                <tr class="cursor-pointer" :class="{'row-open': expanded === s.id}" tabindex="0" role="button"
                    :aria-expanded="expanded === s.id"
                    :aria-label="`Shift on ${f.fmtDay(s.started_at)}, ${shiftStatus(s).label}`"
                    @click="toggle(s.id)" @keydown.enter.prevent="toggle(s.id)"
                    @keydown.space.prevent="toggle(s.id)">
                    <td>
                        <v-icon size="small">{{ expanded === s.id ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                    </td>
                    <td class="text-no-wrap">{{ f.fmtDay(s.started_at) }}</td>
                    <td v-if="showDriver">
                        <router-link class="text-primary text-decoration-none"
                                     :to="{path: `/drivers/${s.driver_id}`, query: {tab: 'shifts'}}"
                                     @click.stop @keydown.stop>
                            {{ s.driver_name || 'Driver' }}
                        </router-link>
                    </td>
                    <td class="text-no-wrap">
                        {{ f.fmtTime(s.started_at) }} to
                        {{ s.ended_at ? f.fmtTimeAfter(s.ended_at, s.started_at) : 'now' }}
                    </td>
                    <td>{{ s.asset_label || '--' }}</td>
                    <td class="text-right font-weight-medium text-no-wrap">{{ fmtDuration(s.worked_seconds) }}</td>
                    <td class="text-right text-no-wrap">
                        {{ Number(s.break_seconds) > 0 ? fmtDuration(s.break_seconds) : '--' }}
                    </td>
                    <td>
                        <v-chip size="small" :color="shiftStatus(s).color" variant="tonal"
                                :prepend-icon="shiftStatus(s).icon">
                            {{ shiftStatus(s).label }}
                        </v-chip>
                    </td>
                </tr>
                <tr v-if="expanded === s.id" class="detail-row">
                    <td :colspan="columnCount" class="pa-0">
                        <!-- Held to the visible width, so on a narrow window the
                             review stays on screen while the table scrolls sideways. -->
                        <div class="detail-holder">
                            <ShiftDetail :shift-id="s.id" :company-id="companyId" @reviewed="onReviewed"/>
                        </div>
                    </td>
                </tr>
            </template>
            </tbody>
        </v-table>

        <div v-else-if="!loading && !loadError" class="pa-8 text-center text-medium-emphasis">
            <v-icon size="40" class="mb-2">{{ status === 'review' ? 'mdi-check-all' : 'mdi-clock-outline' }}</v-icon>
            <div class="text-body-1">{{ emptyText }}</div>
            <div v-if="emptyHint" class="text-body-2 mt-1">{{ emptyHint }}</div>
        </div>

        <div v-if="loading && !rows.length" class="pa-6 text-center">
            <v-progress-circular indeterminate color="primary"/>
        </div>

        <div v-if="lastPage > 1" class="d-flex justify-center py-3">
            <v-pagination v-model="page" :length="lastPage" :total-visible="compact ? 4 : 6" density="compact"/>
        </div>
    </v-card>
</template>

<script setup>
import {computed, onBeforeUnmount, onMounted, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import {useDisplay} from "vuetify";
import {send} from "@/composables/apiClient";
import {companyTimezone} from "@/composables/useTimezone";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {useReportExport} from "@/composables/useReportExport";
import ShiftDetail from "./ShiftDetail.vue";
import {
    apiError, fmtDuration, presetRange, rangePhrase, RANGE_PRESETS, shiftFormatter, shiftStatus, STATUS_FILTERS,
    validYmd,
} from "./shiftFormat";

const props = defineProps({
    // One driver's shifts; all of the company's when not given.
    driverId: {type: [Number, String], default: null},
    // That driver's name, for the empty state.
    driverName: {type: String, default: ""},
    // Whose shifts, for a platform admin; a company user's own company otherwise.
    companyId: {type: [Number, String], default: null},
    showDriver: {type: Boolean, default: false},
    title: {type: String, default: "Shifts"},
    // Open on the review queue rather than everything.
    startOnReview: {type: Boolean, default: false},
    // Keep the filters in the page URL, so a search can be bookmarked. Only
    // for the Fleet Shifts page; the driver tab keeps them in the component.
    syncQuery: {type: Boolean, default: false},
});
const emit = defineEmits(["review-count"]);

const route = useRoute();
const router = useRouter();
const {showSnackbar} = useSnackbarStore();
const {handleDownloadResponse} = useReportExport();

// On a phone the table gives way to a stacked list.
const {xs} = useDisplay();
const compact = computed(() => xs.value);
// Open, date, time, vehicle, worked, breaks and status, plus driver.
const columnCount = computed(() => 7 + (props.showDriver ? 1 : 0));

// ---- filters ---------------------------------------------------------------

const presetValues = RANGE_PRESETS.map((p) => p.value);
const statusValues = STATUS_FILTERS.map((s) => s.value);
const initial = props.syncQuery ? route.query : {};
const str = (v) => (Array.isArray(v) ? v[0] : v) ?? "";

const opensOnReview = str(initial.status) === "review" || (!str(initial.status) && props.startOnReview);
// The review queue opens on all dates, so a manager never misses an old flag;
// a range in the URL (a bookmarked search) still wins.
const preset = ref(presetValues.includes(str(initial.range))
    ? str(initial.range)
    : (opensOnReview ? "all" : "this_month"));
const customFrom = ref(validYmd(str(initial.from)) ? str(initial.from) : "");
const customTo = ref(validYmd(str(initial.to)) ? str(initial.to) : "");
const status = ref(
    statusValues.includes(str(initial.status)) ? str(initial.status) : (props.startOnReview ? "review" : "all")
);
const vehicle = ref(/^\d+$/.test(str(initial.vehicle)) ? Number(str(initial.vehicle)) : null);
const search = ref(props.driverId ? "" : str(initial.q).slice(0, 100));
// The search the list was loaded with: typing waits for a pause.
const searchApplied = ref(search.value.trim());
const sort = ref(str(initial.sort) === "asc" ? "asc" : "desc");

// "On shift now" is not cut by date: a shift that began yesterday is still
// running. "Needs review" is, so its count and its list are the same search.
const isQueue = computed(() => STATUS_FILTERS.find((s) => s.value === status.value)?.queue === true);
const quickChip = computed(() => (["all", "open", "review"].includes(status.value) ? status.value : null));
const onQuickChip = (v) => {
    status.value = v || "all";
};

const zone = ref(null);
const tz = computed(() => zone.value || companyTimezone());
const f = computed(() => shiftFormatter(tz.value));

const range = computed(() => {
    if (preset.value === "custom") return {from: customFrom.value, to: customTo.value};
    return presetRange(preset.value, tz.value) ?? {from: "", to: ""};
});
// Why Export is off, or "" when it is on.
const exportBlocked = computed(() => {
    if (isQueue.value) return "Pick a status other than On shift now to export";
    if (customInvalid.value) return "The end date is before the start date";
    if (!range.value.from || !range.value.to) return "Pick a date range of up to a year to export";
    return "";
});
const customInvalid = computed(() =>
    preset.value === "custom" && !!customFrom.value && !!customTo.value && customFrom.value > customTo.value);

// The filters as API parameters; the same set drives the list and the export.
const filterParams = () => {
    const p = {};
    if (props.driverId) p.driver = String(props.driverId);
    if (props.companyId) p.company = String(props.companyId);
    if (status.value === "review") p.needs_review = "1";
    else if (status.value === "open") p.open = "1";
    else if (status.value !== "all") p.status = status.value;
    if (!isQueue.value) {
        if (range.value.from) p.from = range.value.from;
        if (range.value.to) p.to = range.value.to;
    }
    if (vehicle.value) p.vehicle = String(vehicle.value);
    if (!props.driverId && searchApplied.value) p.q = searchApplied.value;
    if (sort.value === "asc") p.sort = "asc";
    return p;
};

// ---- list ------------------------------------------------------------------

const rows = ref([]);
const loading = ref(false);
const loadError = ref("");
const page = ref(1);
const lastPage = ref(1);
// The whole queue (the driver tab's badge) and the part of it inside this
// search (the banner and the chip, which match the list "Show" opens).
const needsReviewCount = ref(0);
const reviewInSearch = ref(0);
const totals = ref(null);
const expanded = ref(null);

const vehicles = ref([]);
const vehiclesLoading = ref(false);
const vehicleLabel = computed(() => vehicles.value.find((v) => v.id === vehicle.value)?.label || "");

// "No shifts for Ana Lopez last week", built from whatever is filtering.
const emptyText = computed(() => {
    const who = props.driverId
        ? (props.driverName ? ` for ${props.driverName}` : "")
        : (searchApplied.value ? ` for drivers matching "${searchApplied.value}"` : "");
    const on = vehicleLabel.value ? ` on ${vehicleLabel.value}` : "";
    const when = rangePhrase(preset.value, range.value.from, range.value.to);
    if (status.value === "review") return `Nothing to review${who}${on}${when ? " " + when : ""}.`;
    if (status.value === "open") {
        if (props.driverId) return `${props.driverName || "This driver"} is not on shift${on} right now.`;
        const matching = searchApplied.value ? ` matching "${searchApplied.value}"` : "";
        return `Nobody${matching} is on shift${on} right now.`;
    }
    const noun = STATUS_FILTERS.find((s) => s.value === status.value)?.noun || "shifts";
    return `No ${noun}${who}${on}${when ? " " + when : ""}.`;
});
const emptyHint = computed(() => {
    if (status.value !== "all" || vehicle.value || searchApplied.value) return "";
    return props.driverId
        ? "Shifts appear here once the driver clocks in with the SimpliGPS Driver app."
        : "Shifts appear here once drivers clock in with the SimpliGPS Driver app.";
});

// Guards against an older response landing after a newer one.
let requestSeq = 0;

const fetchRows = () => {
    const seq = ++requestSeq;
    if (customInvalid.value) {
        rows.value = [];
        totals.value = null;
        lastPage.value = 1;
        loading.value = false;
        return;
    }
    loading.value = true;
    loadError.value = "";
    const query = new URLSearchParams({...filterParams(), page: String(page.value)});

    send("get", `fleet/duty/sessions?${query.toString()}`)
        .then((r) => {
            if (seq !== requestSeq) return;
            const d = r.data.data ?? {};
            rows.value = d.data ?? [];
            lastPage.value = d.last_page ?? 1;
            zone.value = d.company_timezone ?? null;
            totals.value = d.totals ?? null;
            needsReviewCount.value = d.needs_review_count ?? 0;
            reviewInSearch.value = d.needs_review_in_search ?? needsReviewCount.value;
            emit("review-count", needsReviewCount.value);
            // A page that emptied (its last shift was just reviewed) steps back.
            if (!rows.value.length && page.value > 1) page.value = Math.min(page.value - 1, lastPage.value);
        })
        .catch((e) => {
            if (seq !== requestSeq) return;
            loadError.value = apiError(e, "Shifts could not be loaded.");
        })
        .finally(() => {
            if (seq === requestSeq) loading.value = false;
        });
};

const fetchVehicles = () => {
    vehiclesLoading.value = true;
    // Records stay on trucks that were disabled or deleted since.
    const params = {include_deleted: 1};
    if (props.companyId) params.company = props.companyId;
    send("get", "assets/list", params)
        .then((r) => {
            const list = r?.data?.data ?? [];
            vehicles.value = (Array.isArray(list) ? list : [])
                .map((a) => {
                    const name = a.label || `Vehicle ${a.id}`;
                    const note = a.deleted_at ? " (deleted)" : (a.status && a.status !== "enabled" ? " (disabled)" : "");
                    return {id: a.id, label: name + note};
                })
                .sort((a, b) => a.label.localeCompare(b.label));
        })
        .catch(() => {
            vehicles.value = [];
        })
        .finally(() => {
            vehiclesLoading.value = false;
        });
};

const toggle = (id) => {
    expanded.value = expanded.value === id ? null : id;
};

// The row keeps its place until the list is next loaded, so the manager sees
// the outcome under the row they acted on.
const onReviewed = (shift) => {
    const i = rows.value.findIndex((r) => r.id === shift.id);
    // The badge counts flagged shifts only.
    const wasFlagged = i !== -1 && rows.value[i].needs_review;
    if (i !== -1) rows.value[i] = {...rows.value[i], ...shift};
    if (wasFlagged && !shift.needs_review) {
        needsReviewCount.value = Math.max(0, needsReviewCount.value - 1);
        reviewInSearch.value = Math.max(0, reviewInSearch.value - 1);
    }
    emit("review-count", needsReviewCount.value);
};

// ---- export ----------------------------------------------------------------

const exporting = ref(false);
let searchTimer = null;

// The app's shared report download (useReportExport): the same filters as
// the list, through the authenticated client, so the token stays in the
// header and never in a URL.
const exportCsv = async () => {
    if (customInvalid.value) return;
    // The search as typed, not as last debounced.
    clearTimeout(searchTimer);
    searchApplied.value = (search.value || "").trim().slice(0, 100);
    exporting.value = true;
    const params = filterParams();
    try {
        const response = await send("get", "fleet/duty/sessions/export", params, {responseType: "text"});
        // Excel reads accented names as UTF-8 only with a byte order mark,
        // which decoding the response as text drops. Added for this file
        // only; the shared helper is left as the other reports use it.
        const text = String(response?.data ?? "");
        const data = text.startsWith("\uFEFF") ? text : "\uFEFF" + text;
        handleDownloadResponse({data}, `shifts_${params.from}_to_${params.to}.csv`);
    } catch (e) {
        // A text response: a refusal arrives as the JSON string.
        let data = e?.response?.data;
        try {
            if (typeof data === "string") data = JSON.parse(data);
        } catch {
            data = null;
        }
        showSnackbar(apiError({response: {...e?.response, data}}, "The export could not be made."), "error");
    } finally {
        exporting.value = false;
    }
};

// ---- reacting to the filters -------------------------------------------------

// Back to the first page, which fetches through the page watcher; or fetch
// here when already on it.
const restart = () => {
    expanded.value = null;
    if (page.value !== 1) page.value = 1;
    else fetchRows();
};

// The filters in the URL, defaults left out, so a manager can bookmark a search.
const writeQuery = () => {
    if (!props.syncQuery) return;
    const q = {...route.query};
    delete q.review;
    const set = (k, v, dflt) => {
        if (v && v !== dflt) q[k] = String(v);
        else delete q[k];
    };
    set("range", preset.value, status.value === "review" ? null : "this_month");
    set("from", preset.value === "custom" ? customFrom.value : "", "");
    set("to", preset.value === "custom" ? customTo.value : "", "");
    set("status", status.value, "all");
    set("vehicle", vehicle.value, null);
    set("q", searchApplied.value, "");
    set("sort", sort.value, "desc");
    router.replace({query: q}).catch(() => {});
};

watch(search, (v) => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
        searchApplied.value = (v || "").trim().slice(0, 100);
    }, 350);
});

// Choosing "Needs review" (the chip, the select or a link) widens to all
// dates. "Show" on the banner keeps the dates: its count is this search's.
let keepRangeOnReview = false;
const showReview = () => {
    keepRangeOnReview = true;
    status.value = "review";
};
watch(status, (now) => {
    if (now === "review" && !keepRangeOnReview) preset.value = "all";
    keepRangeOnReview = false;
});

watch([preset, customFrom, customTo, status, vehicle, searchApplied, sort], () => {
    writeQuery();
    restart();
});
// The first load guesses the fleet's zone; the API answers with the real one.
// When that moves "today" to another day, load the right days.
watch(tz, (now, before) => {
    if (preset.value === "custom" || isQueue.value) return;
    const a = presetRange(preset.value, now);
    const b = presetRange(preset.value, before);
    if (a?.from !== b?.from || a?.to !== b?.to) restart();
});
watch(page, () => {
    expanded.value = null;
    fetchRows();
});
watch(() => props.driverId, restart);
watch(() => props.companyId, () => {
    vehicles.value = [];
    fetchVehicles();
    // Clearing a chosen vehicle restarts the list through the filter
    // watcher; with none chosen, restart here. One fetch either way.
    if (vehicle.value !== null) vehicle.value = null;
    else restart();
});
// Following a link to the review queue while already on the page.
watch(() => props.startOnReview, (review) => {
    if (!review) return;
    // The whole queue, as the menu count counts it: no vehicle or name kept.
    vehicle.value = null;
    search.value = "";
    searchApplied.value = "";
    preset.value = "all";
    status.value = "review";
});

onMounted(() => {
    fetchRows();
    fetchVehicles();
});
onBeforeUnmount(() => clearTimeout(searchTimer));

defineExpose({refresh: fetchRows});
</script>

<style scoped>
.cursor-pointer {
    cursor: pointer;
}

.row-open > td {
    background: rgba(var(--v-theme-primary), 0.05);
}

.shift-table tr:focus-visible {
    outline: 2px solid rgb(var(--v-theme-primary));
    outline-offset: -2px;
}

.detail-row:hover > td {
    background: transparent !important;
}

.detail-holder {
    position: sticky;
    left: 0;
    max-width: calc(100vw - 32px);
}

.detail-holder-phone {
    background: rgba(var(--v-theme-primary), 0.03);
}

.totals-strip :deep(.v-chip) {
    max-width: 100%;
}
</style>
